import { useState, ChangeEvent, FormEvent, ReactElement } from 'react'
import * as Styled from './Wallet.styled'
import { computeCardFee } from './cardFee'

interface Props {
  min: number
  max: number
  presets: number[]
  feeRate: number
  feeThreshold: number
  onSubmit: (amount: number) => void
  onCancel: () => void
}

export default function AmountInputModal({
  min,
  max,
  presets,
  feeRate,
  feeThreshold,
  onSubmit,
  onCancel,
}: Props): ReactElement {
  const [selected, setSelected] = useState<number | null>(null)
  const [custom, setCustom] = useState('')
  const [error, setError] = useState<string | null>(null)

  function pickPreset(amt: number): void {
    setSelected(amt)
    setCustom('')
    setError(null)
  }

  function onCustomChange(e: ChangeEvent<HTMLInputElement>): void {
    const raw = e.target.value.replace(/[^0-9]/g, '')
    setCustom(raw)
    setSelected(null)
    setError(null)
  }

  function submit(e: FormEvent): void {
    e.preventDefault()
    const amount = selected ?? (custom ? parseInt(custom, 10) : NaN)
    if (!Number.isFinite(amount) || amount <= 0) {
      setError('Please select or enter an amount')
      return
    }
    if (!Number.isInteger(amount)) {
      setError('Amount must be a whole number')
      return
    }
    if (amount < min) {
      setError(`Minimum ฿${min.toLocaleString()}`)
      return
    }
    if (amount > max) {
      setError(`Maximum ฿${max.toLocaleString()}`)
      return
    }
    onSubmit(amount)
  }

  const draftAmount = selected ?? (custom ? parseInt(custom, 10) : NaN)
  const feePreview =
    feeRate > 0 && Number.isFinite(draftAmount) && draftAmount > 0
      ? computeCardFee(draftAmount, { rate: feeRate, threshold: feeThreshold })
      : null

  return (
    <Styled.ModalOverlay onClick={onCancel}>
      <Styled.ModalCard onClick={(e) => e.stopPropagation()}>
        <Styled.ModalTitle>Select Top-Up Amount</Styled.ModalTitle>

        <form onSubmit={submit}>
          <Styled.PresetGrid>
            {presets.map((amt) => (
              <Styled.PresetBtn
                key={amt}
                type='button'
                selected={selected === amt}
                onClick={() => pickPreset(amt)}
              >
                ฿{amt.toLocaleString()}
              </Styled.PresetBtn>
            ))}
          </Styled.PresetGrid>

          <Styled.AmountInput
            type='text'
            inputMode='numeric'
            placeholder={`Or enter custom amount (฿${min.toLocaleString()} – ฿${max.toLocaleString()})`}
            value={custom}
            onChange={onCustomChange}
          />

          {feeRate > 0 && (
            <div style={{ fontSize: 12, color: '#78716c', margin: '8px 0 0' }}>
              Top-ups of ฿{feeThreshold.toLocaleString()}+ have no card fee ·
              below that a {feeRate}% card fee applies
            </div>
          )}
          {feePreview && feePreview.fee > 0 && (
            <div style={{ fontSize: 13, color: '#44403c', margin: '6px 0 0', fontWeight: 600 }}>
              You&apos;ll pay ฿{feePreview.chargeTotal.toLocaleString()} (includes ฿
              {feePreview.fee.toLocaleString()} card fee)
            </div>
          )}
          {feePreview && feePreview.fee === 0 && draftAmount >= feeThreshold && feeRate > 0 && (
            <div style={{ fontSize: 13, color: '#0f766e', margin: '6px 0 0' }}>
              No card fee ✓
            </div>
          )}

          {error && <Styled.FormError>{error}</Styled.FormError>}

          <Styled.SubmitBtn type='submit'>Next</Styled.SubmitBtn>
          <Styled.ModalCloseBtn type='button' onClick={onCancel}>
            Cancel
          </Styled.ModalCloseBtn>
        </form>
      </Styled.ModalCard>
    </Styled.ModalOverlay>
  )
}
